/**
 * docsBatchLoader
 * ---------------
 * Coalesces N per-row `GET /documents/for/{entity_type}/{entity_id}`
 * requests into a single `POST /documents/for-batch` call. The Bank
 * Feed review table renders one AttachDocsButton per transaction row -
 * with 100+ rows the previous fan-out spammed the backend with 100+
 * concurrent GETs, exhausting Uvicorn's single-worker connection pool
 * and producing "Our servers are temporarily unavailable" login
 * failures on other tabs.
 *
 * Every caller registers via `loadDocsFor(entityType, entityId)` and
 * receives a Promise that resolves with `{count, documents}` shaped
 * identically to the single-entity endpoint. Requests registered
 * within FLUSH_MS of each other are batched together (deduped by
 * `type:id` key) and flushed as one POST. If the batch endpoint 404s
 * we fall back to the single endpoint - keeps things working in dev
 * where the backend hasn't reloaded yet.
 */
import axios from 'axios';
import { API } from '../../App';

const FLUSH_MS = 30;        // debounce window
const MAX_BATCH = 300;      // hard ceiling per POST

let pending = new Map();    // key -> [{resolve, reject}]
let flushTimer = null;
let batchSupported = true;  // flips to false after a 404/405

const keyOf = (t, id) => `${t}:${id}`;

const flush = async () => {
  flushTimer = null;
  if (pending.size === 0) return;
  // Take up to MAX_BATCH; anything above stays for next tick.
  const batch = new Map();
  for (const [k, waiters] of pending.entries()) {
    batch.set(k, waiters);
    if (batch.size >= MAX_BATCH) break;
  }
  for (const k of batch.keys()) pending.delete(k);
  if (pending.size > 0) schedule();

  const items = Array.from(batch.keys()).map((k) => {
    const [entity_type, ...rest] = k.split(':');
    return { entity_type, entity_id: rest.join(':') };
  });

  const resolveKey = (k, payload) => {
    const waiters = batch.get(k) || [];
    for (const w of waiters) w.resolve(payload);
  };
  const rejectKey = (k, err) => {
    const waiters = batch.get(k) || [];
    for (const w of waiters) w.reject(err);
  };

  try {
    if (batchSupported) {
      const r = await axios.post(`${API}/documents/for-batch`, { items });
      const results = r?.data?.results || {};
      for (const k of batch.keys()) {
        const payload = results[k] || { count: 0, documents: [] };
        resolveKey(k, payload);
      }
      return;
    }
  } catch (err) {
    const status = err?.response?.status;
    if (status === 404 || status === 405) {
      // Old backend without the batch route - fall back forever.
      batchSupported = false;
    } else {
      // Transient error - resolve every waiter with an empty result so
      // AttachDocsButton stays rendered without a badge instead of
      // throwing.
      for (const k of batch.keys()) resolveKey(k, { count: 0, documents: [] });
      return;
    }
  }

  // Fallback: fire one-shot GETs (rare - only when server too old).
  await Promise.all(Array.from(batch.keys()).map(async (k) => {
    const [entity_type, ...rest] = k.split(':');
    const entity_id = rest.join(':');
    try {
      const r = await axios.get(`${API}/documents/for/${entity_type}/${entity_id}`);
      resolveKey(k, {
        count: Number(r.data?.count) || 0,
        documents: Array.isArray(r.data?.documents) ? r.data.documents : [],
      });
    } catch (err) {
      rejectKey(k, err);
    }
  }));
};

const schedule = () => {
  if (flushTimer) return;
  flushTimer = setTimeout(flush, FLUSH_MS);
};

export const loadDocsFor = (entityType, entityId) => {
  if (!entityType || !entityId) {
    return Promise.resolve({ count: 0, documents: [] });
  }
  const k = keyOf(entityType, entityId);
  return new Promise((resolve, reject) => {
    const list = pending.get(k) || [];
    list.push({ resolve, reject });
    pending.set(k, list);
    schedule();
  });
};
